import React, { useImperativeHandle, useRef, useState } from 'react';
import { Button, Form, Modal, Spin, Table, message, Input } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { fetchGiveAwayCoupon, fetchGiveAwayCouponList } from '@/api/guest';
import { CycleNameMap } from '@/config';
import { renderIntlLabel } from '@/utils';
import CustomDrawer from '../CustomDrawer';
import { EmptyHint } from '../EmptyHint';
import PresentConfirmDrawer, { PresentConfirmImperativeHandle } from './PresentConfirmDrawer';
import { CouponDescriptionGetter } from './helper';
import styles from './index.module.scss';

export interface imperativeHandle {
  openDrawer: (data: { record: object; onOk: () => void }) => void;
}

const PresentedCouponDrawer = React.forwardRef<imperativeHandle, any>((props, ref) => {
  const [drawerVisible, setDrawerVisible] = useState<boolean>(false);
  const [useInfo, setUseInfo] = useState<any>({});

  const [searchStatus, setSearchStatus] = useState<'init' | 'noData' | 'showData'>('init');
  const [dataSource, setDataSource] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [open, setOpen] = useState<boolean>(false);
  const [detail, setDetail] = useState<any>({});
  const [giveLoading, setGiveLoading] = useState<boolean>(false);

  const paramsRef = useRef<any>();
  const confirmRef = useRef<PresentConfirmImperativeHandle>();

  const [giveForm] = Form.useForm();

  const initCouponList = async () => {
    try {
      setLoading(true);
      const res = await fetchGiveAwayCouponList({});
      if (res.code === 0) {
        setDataSource(res.data || []);
        if (Array.isArray(res.data) && res.data.length) {
          setSearchStatus('showData');
        } else {
          setSearchStatus('noData');
        }
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleOpenDrawer = (data) => {
    const { record = {}, onOk } = data || {};
    setUseInfo(record);
    paramsRef.current = onOk;
    setDrawerVisible(true);

    initCouponList();
  };

  const handleCloseDrawer = () => {
    setSearchStatus('init');
    setDataSource([]);
    setLoading(false);

    setDrawerVisible(false);

    if (typeof paramsRef.current === 'function') {
      paramsRef.current();
    }
  };

  const handleOpenGiveAwayModal = (record) => {
    setDetail(record);

    confirmRef.current.openDrawer({
      useInfo,
      record,
      onOk: () => {
        initCouponList();
      },
    });
    // giveForm.resetFields();
    // setOpen(true);
  };

  const handleOK = async () => {
    const { wyuid } = useInfo;
    const {
      couponCode,
      couponFaceValueText,
      couponName,
      shopGiveCouponRangeConfigId,
      shopGiveCouponRuleId,
    } = detail;
    const reason = giveForm.getFieldValue('reason');
    if (!reason) {
      message.error(renderIntlLabel('guest_coupon_placeholder_please_input_give_reason'));
      return;
    }
    try {
      setGiveLoading(true);
      const params = {
        wyuid,
        quantity: 1,
        couponCode,
        couponDescribe: couponFaceValueText,
        couponName,
        shopGiveCouponRangeConfigId,
        shopGiveCouponRuleId,
        reason,
      };
      const res = await fetchGiveAwayCoupon(params);
      if (res.code === 0) {
        message.success(renderIntlLabel('guest_msg_give_coupon_successfully'));
        setOpen(false);
        initCouponList();
      }
      setGiveLoading(false);
    } catch (error) {
      setGiveLoading(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  useImperativeHandle(
    ref,
    () => ({
      openDrawer: handleOpenDrawer,
    }),
    [],
  );

  const columns: ColumnsType<any> = [
    {
      title: renderIntlLabel('guest_coupon_label_shopGiveCouponConfigName'),
      dataIndex: 'shopGiveCouponConfigName',
    },
    {
      title: renderIntlLabel('guest_coupon_label_couponName'),
      dataIndex: 'couponName',
    },
    {
      title: renderIntlLabel('guest_coupon_label_couponFaceValue'),
      dataIndex: 'couponFaceValueText',
    },
    {
      title: renderIntlLabel('guest_coupon_label_avalible_effective_range'),
      key: 'enableDateRange',
      render: (_, record) => CouponDescriptionGetter.useTimeDesc(record),
    },
    {
      title: renderIntlLabel('guest_coupon_label_avalible_store_range'),
      key: 'commonCodeRange',
      render: (_, record) => CouponDescriptionGetter.useStoreDesc(record),
    },
    {
      title: renderIntlLabel('guest_coupon_label_effective'),
      key: 'effectiveDateRange',
      render: (_, record) => CouponDescriptionGetter.baseLifeDesc(record),
    },
    {
      title: renderIntlLabel('guest_coupon_label_limit_of_give'),
      dataIndex: 'giftRestrictions',
      render: (_, record) =>
        renderIntlLabel('guest_coupon_label_limit_count_of_coupons', {
          typeName: CycleNameMap[record.type],
          giftRestrictions: record.giftRestrictions,
        }),
    },
    {
      title: renderIntlLabel('guest_coupon_label_remaining_quantity_of_give'),
      dataIndex: 'residueNumber',
      render: (val) =>
        renderIntlLabel('guest_coupon_label_remaining_quantity_now', { val: val || 0 }),
    },
    {
      title: renderIntlLabel('order_action'),
      render: (_, record) => (
        <Button type="link" onClick={() => handleOpenGiveAwayModal(record)}>
          {renderIntlLabel('guest_action_give')}
        </Button>
      ),
      fixed: 'right',
    },
  ];

  return (
    <>
      <CustomDrawer
        title={renderIntlLabel('guest_title_give_wangyu_coupon')}
        footer={
          <Button type="ghost" onClick={handleCloseDrawer}>
            {renderIntlLabel('order_action_close')}
          </Button>
        }
        width="70%"
        open={drawerVisible}
        onClose={handleCloseDrawer}
      >
        <div className={styles.container}>
          <div style={{ marginBottom: '32px' }}>
            <div className="groupLabel" style={{ margin: '0 0 16px' }}>
              {renderIntlLabel('guest_label_Grantee_information')}
            </div>
            <div>
              <Form layout="inline">
                <Form.Item label={renderIntlLabel('order_roomorder_name')}>
                  {useInfo?.name}
                </Form.Item>
                <Form.Item label={renderIntlLabel('order_roomorder_cert_no')}>
                  {useInfo?.certNo}
                </Form.Item>
                {/* <Form.Item label={renderIntlLabel('order_roomorder_mobile')}>
                  {useInfo?.mobile}
                </Form.Item> */}
              </Form>
            </div>
          </div>
          <div>
            <div className="groupLabel" style={{ margin: '0 0 16px' }}>
              {renderIntlLabel('guest_coupon_label_selection_of_give')}
            </div>
            <Spin spinning={loading}>
              {searchStatus === 'showData' ? (
                <Table
                  rowKey="couponCode"
                  columns={columns}
                  dataSource={dataSource}
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                />
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '80px' }}>
                  <EmptyHint hintText={renderIntlLabel('guest_title_give_avalible_coupon_now')} />
                </div>
              )}
            </Spin>
          </div>

          <PresentConfirmDrawer ref={confirmRef} />

          <Modal
            title={renderIntlLabel('guest_title_confirm_of_give')}
            okText={renderIntlLabel('guest_action_confirm_to_give')}
            maskClosable={false}
            open={open}
            okButtonProps={{ loading: giveLoading }}
            onOk={handleOK}
            onCancel={handleCancel}
          >
            {renderIntlLabel('guest_coupon_label_give_conpon_to_someone', {
              name: useInfo?.name,
              certNo: useInfo?.certNo,
              // mobile: useInfo?.mobile,
              couponName: detail?.couponName,
            })}
            {/* {`即将向${useInfo?.name}，${useInfo?.certNo}，${useInfo?.mobile}赠送优惠券【${detail?.couponName}】x 1，赠送后无法撤回`} */}
            <Form form={giveForm} style={{ marginTop: 24 }}>
              <Form.Item name="reason" label={renderIntlLabel('guest_coupon_label_giveReason')}>
                <Input.TextArea
                  maxLength={10000}
                  rows={5}
                  placeholder={renderIntlLabel('guest_coupon_placeholder_giveReason_required')}
                />
              </Form.Item>
            </Form>
          </Modal>
        </div>
      </CustomDrawer>
    </>
  );
});

export default PresentedCouponDrawer;
